import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { reports } from "../../api/endpoints";
import ModelBadge from "../../components/ModelBadge.jsx";
import { useSelectedWebsite } from "./Layout.jsx";

export default function Answers() {
  const { t } = useTranslation();
  const { websiteId } = useSelectedWebsite();
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ count: 0, results: [], next: null, previous: null });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!websiteId) return;
    setLoading(true);
    reports
      .answers(websiteId, page, 25)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [websiteId, page]);

  if (!websiteId)
    return <div className="text-slate-500">{t("prompts.pickWebsite")}</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t("answers.title")}</h1>
        <p className="text-sm text-slate-600">{t("answers.subtitle")}</p>
      </div>

      {/* overflow-x-auto + min-w keep the table scrollable on
          narrow viewports instead of clipped by the card. */}
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("answers.colPrompt")}</th>
              <th className="px-4 py-3 font-medium">{t("answers.colModel")}</th>
              <th className="px-4 py-3 font-medium">{t("answers.colBrand")}</th>
              <th className="px-4 py-3 font-medium">{t("answers.colSentiment")}</th>
              <th className="px-4 py-3 font-medium">{t("answers.colScore")}</th>
              <th className="px-4 py-3 font-medium">{t("answers.colWhen")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-4 py-6 text-slate-500">
                  {t("answers.loading")}
                </td>
              </tr>
            ) : data.results.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-6 text-slate-500">
                  {t("answers.noScans")}
                </td>
              </tr>
            ) : (
              data.results.flatMap((job) => {
                const r = job.result;
                const isOpen = expanded === job.id;
                return [
                  <tr
                    key={job.id}
                    onClick={() => setExpanded(isOpen ? null : job.id)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className="px-4 py-3 max-w-md truncate">
                      {job.prompt_text || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <ModelBadge modelKey={job.ai_model} size="sm" />
                    </td>
                    <td className="px-4 py-3">{r?.brand_mentioned ? "✓" : "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ring-1 sentiment-${r?.sentiment || "unknown"}`}
                      >
                        {r?.sentiment || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 mono">
                      {r?.ai_visibility_score?.toFixed(2) ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {job.completed_at
                        ? new Date(job.completed_at).toLocaleString()
                        : "—"}
                    </td>
                  </tr>,
                  isOpen && (
                    <tr key={`${job.id}-x`} className="bg-slate-50">
                      <td colSpan="6" className="px-4 py-4 text-sm space-y-4">
                        {r?.mention_positions?.length > 0 && (
                          <div>
                            <div className="text-xs uppercase text-slate-500 mb-1">
                              {t("answers.snippet")}
                            </div>
                            <div className="bg-amber-50 px-3 py-2 rounded">
                              … {r.mention_positions[0].snippet} …
                            </div>
                          </div>
                        )}
                        {r?.extracted_urls?.length > 0 && (
                          <div>
                            <div className="text-xs uppercase text-slate-500 mb-1">
                              {t("answers.citedUrls")}
                            </div>
                            <ul className="space-y-1">
                              {r.extracted_urls.map((u, i) => (
                                <li key={i}>
                                  <a
                                    href={u.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-brand-700 hover:underline break-all"
                                  >
                                    {u.url}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div>
                          <div className="text-xs uppercase text-slate-500 mb-1">
                            {t("answers.fullResponse")}
                          </div>
                          {r?.raw_response ? (
                            <pre className="bg-white border border-slate-200 rounded p-3 whitespace-pre-wrap break-words text-sm text-slate-800 max-h-96 overflow-y-auto">
                              {r.raw_response}
                            </pre>
                          ) : (
                            <div className="text-slate-500 italic">
                              {t("answers.responseRetained")}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ),
                ].filter(Boolean);
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-500">
          {t("answers.totalScans", { count: data.count })}
        </span>
        <div className="space-x-2">
          <button
            disabled={!data.previous}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="btn-ghost disabled:opacity-40"
          >
            ← {t("common.previous")}
          </button>
          <button
            disabled={!data.next}
            onClick={() => setPage((p) => p + 1)}
            className="btn-ghost disabled:opacity-40"
          >
            {t("common.next")} →
          </button>
        </div>
      </div>
    </div>
  );
}
